<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="3">
              <DatePicker
                ref="confirmedShukkaDate"
                v-model="confirmedShukkaDate"
                :label="$t('shukkaPreDetail.label.confirmedShukkaDate')"
                :minDate="confirmedShukkaMinDate"
                :maxDate="confirmedShukkaMaxDate"
              />
            </v-col>
            <v-col cols="9" class="text-right">
              <v-btn text @click="back">{{ $t('common.action.back') }}</v-btn>
              <v-btn
                class="ml-4 px-6 bg-red-lighten-1"
                elevation="2"
                @click="deleteSlip"
                :disabled="isSubmitting || dataList.length === 0"
              >
                {{ $t('common.action.delete') }}
              </v-btn>
              <v-btn
                class="ml-4 px-6 bg-grey-darken-3"
                theme="dark"
                elevation="2"
                @click="confirmSlip"
                :loading="isSubmitting"
                :disabled="dataList.length === 0"
              >
                {{ $t('common.action.confirm') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th>{{ $t('shukkaPreDetail.label.table.denpyoNumber') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.shukkaDate') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.shukkamotoCode') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.shukkamotoName') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.shukkasakiCode') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.shukkasakiName') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.janCode') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.productName') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.productCode') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.colorName') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.sizeName') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.planSuuryo') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.actualSuuryo') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.comment') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.updatedDatetime') }}</th>
                    <th>{{ $t('shukkaPreDetail.label.table.action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in dataList" :key="item.lineNumber">
                    <td>{{ item.denpyoNumber }}</td>
                    <td>{{ formatDateTime(item.shukkaDate) }}</td>
                    <td>{{ item.shukkamotoCode }}</td>
                    <td>{{ item.shukkamotoName }}</td>
                    <td>{{ item.shukkasakiCode }}</td>
                    <td>{{ item.shukkasakiName }}</td>
                    <td>{{ item.janCode }}</td>
                    <td>{{ item.productName }}</td>
                    <td>{{ item.productCode }}</td>
                    <td>{{ item.colorName }}</td>
                    <td>{{ item.sizeName }}</td>
                    <td>{{ item.planSuuryo }}</td>
                    <td style="min-width: 120px;">
                      <v-text-field
                        density="compact"
                        variant="outlined"
                        hide-details="auto"
                        inputmode="numeric"
                        v-model="editedQuantityMap[item.lineNumber]"
                        @update:modelValue="sanitizeQuantity(item.lineNumber)"
                      />
                    </td>
                    <td>{{ item.comment }}</td>
                    <td>{{ formatDateTime(item.updatedDatetime) }}</td>
                    <td>
                      <v-btn
                        size="small"
                        class="mr-2"
                        @click="saveRow(item)"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                      >
                        {{ $t('common.action.save') }}
                      </v-btn>
                      <v-btn
                        size="small"
                        color="red"
                        @click="deleteRow(item)"
                        :loading="isSubmitting"
                        :disabled="isSubmitting"
                      >
                        {{ $t('common.action.delete') }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="16"></td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="back" />
          <MessageDialog v-if="commonMsgDlgVisible" :dialog-data="dialogData" :handle-ok="closeMessageDialog" />
          <ShukkaPreConfirmDialog
            v-if="confirmDialogVisible"
            :dialog-data="confirmDialogData"
            :handle-ok="confirmSlipOk"
            :handle-cancel="closeConfirmDialog"
          />
        </v-container>
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import AppWrapperView from "@/components/views/AppWrapperView";
import DatePicker from "@/components/DatePicker";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import MessageDialog from "@/components/dialog/MessageDialog";
import ShukkaPreConfirmDialog from "@/components/dialog/ShukkaPreConfirmDialog";
import api from "@/apis/staff";
import store from "@/store";
import router from "@/router";
import Paths from "@/router/indexPaths";
import utils from "@/utils";

export default {
  components: { AppWrapperView, DatePicker, ZeroData, MessageDialog, ShukkaPreConfirmDialog },
  data: () => ({
    dataList: [],
    editedQuantityMap: {},
    confirmedShukkaDate: "",
    confirmedShukkaMinDate: null,
    confirmedShukkaMaxDate: null,
    isSubmitting: false,
    zeroDataWarningDialogFlg: false,
    commonMsgDlgVisible: false,
    dialogData: { message: "", errorList: [] },
    confirmDialogVisible: false,
    confirmDialogData: { message: "" },
    searchDatetime: "",
  }),
  computed: {
    preManagementId() {
      const value = store.getShukkaPreManagementId();
      return value ? Number(value) : null;
    }
  },
  methods: {
    /**
     * 日時文字列の画面表示用形式への変換
     * @param {string} value 表示対象の日時文字列
     * @returns {string} 整形後の日時
     */
    formatDateTime(value) {
      return utils.putSplitWordToCalendarWithTime(value);
    },
    /**
     * 数量入力値からの数値以外の除去
     * @param {number|string} lineNumber 対象行番号
     */
    sanitizeQuantity(lineNumber) {
      const value = this.editedQuantityMap[lineNumber];
      this.editedQuantityMap[lineNumber] = String(value ?? "").replace(/[^0-9]/g, "");
    },
    /**
     * 確定ダイアログのクローズ
     */
    closeConfirmDialog() {
      this.confirmDialogVisible = false;
    },
    /**
     * メッセージダイアログのクローズ
     */
    closeMessageDialog() {
      this.commonMsgDlgVisible = false;
    },
    /**
     * メッセージダイアログへの内容設定と表示
     * @param {string} message 表示メッセージ
     * @param {Array} errorList エラー詳細一覧
     */
    showMessage(message, errorList = []) {
      this.dialogData = { message, errorList };
      this.commonMsgDlgVisible = true;
    },
    /**
     * 確定前出荷一覧画面への戻り
     */
    back() {
      router.push({ name: Paths.SHUKKA_PRE });
    },
    /**
     * APIエラー内容のダイアログへの集約表示
     * @param {object} error APIエラーオブジェクト
     */
    handleError(error) {
      this.showMessage(
        error?.response?.data?.errorMessage ?? this.$t("common.message.errorAndRetry"),
        error?.response?.data?.errorList ?? []
      );
    },
    /**
     * APIレスポンスの業務エラー判定とモーダル表示
     * @param {object} response APIレスポンス
     * @returns {boolean} 業務エラー有無
     */
    handleResponseError(response) {
      if (response?.data?.errorMessage == null) return false;
      this.showMessage(response.data.errorMessage, response?.data?.errorList ?? []);
      return true;
    },
    /**
     * 出荷可能日範囲設定を取得してDatePickerの選択可能範囲へ反映
     */
    async loadConfirmedShukkaDateRange() {
      try {
        const response = await api.post("/config/shipping-date-range/get", {});
        if (this.handleResponseError(response)) {
          this.confirmedShukkaMinDate = null;
          this.confirmedShukkaMaxDate = null;
          return;
        }
        const rangeDays = Number(response?.data?.results?.shippingAvailableDateRangeDays);
        if (!Number.isInteger(rangeDays) || rangeDays < 0 || rangeDays > 365) {
          this.confirmedShukkaMinDate = null;
          this.confirmedShukkaMaxDate = null;
          return;
        }

        const baseDate = this.getTodayStartDate();
        this.confirmedShukkaMinDate = this.addDate(baseDate, -rangeDays);
        this.confirmedShukkaMaxDate = baseDate;
      } catch (error) {
        this.confirmedShukkaMinDate = null;
        this.confirmedShukkaMaxDate = null;
        this.handleError(error);
      }
    },
    /**
     * 当日基準日付の0時化
     * @returns {Date} 時刻を0時にした当日日付
     */
    getTodayStartDate() {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date;
    },
    /**
     * 指定日付への日数加算
     * @param {Date} baseDate 基準日付
     * @param {number} days 加減算日数
     * @returns {Date} 加算後日付
     */
    addDate(baseDate, days) {
      const result = new Date(baseDate);
      result.setDate(result.getDate() + days);
      return result;
    },
    /**
     * 確定前出荷の明細情報取得と編集用データの初期化
     */
    loadDetail() {
      if (!this.preManagementId) {
        this.back();
        return;
      }

      api.post("/shukka/pre/detail/search", {
        preManagementId: this.preManagementId,
      }).then((response) => {
        if (this.handleResponseError(response)) return;

        this.dataList = response.data.results?.shukkaList ?? [];
        this.editedQuantityMap = {};

        if (this.dataList.length === 0) {
          this.zeroDataWarningDialogFlg = true;
          return;
        }

        this.zeroDataWarningDialogFlg = false;
        this.dataList.forEach(item => {
          this.editedQuantityMap[item.lineNumber] = String(item.actualSuuryo ?? "");
        });
        this.searchDatetime = this.getNowYYYYMMDDHHmmss();
      }).catch((error) => {
        this.handleError(error);
      });
    },
    getNowYYYYMMDDHHmmss() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");

      return (
        d.getFullYear() +
        pad(d.getMonth() + 1) +
        pad(d.getDate()) +
        pad(d.getHours()) +
        pad(d.getMinutes()) +
        pad(d.getSeconds())
      );
    },
    /**
     * 指定行の数量変更の保存
     * @param {object} item 更新対象の明細行
     */
    saveRow(item) {
      const newQuantity = this.editedQuantityMap[item.lineNumber];
      this.isSubmitting = true;
      api.post("/shukka/pre/adjust", {
        preManagementId: this.preManagementId,
        lineNo: item.lineNumber,
        janCode: item.janCode,
        newQuantity: newQuantity === "" ? null : Number(newQuantity),
        searchDatetime: this.searchDatetime,
        staffId: "",
      }).then((response) => {
        if (this.handleResponseError(response)) {
          this.isSubmitting = false;
          return;
        }
        this.showMessage(this.$t("shukkaPreDetail.message.saved"));
        this.isSubmitting = false;
        this.loadDetail();
      }).catch((error) => {
        this.isSubmitting = false;
        this.handleError(error);
      });
    },
    /**
     * 指定行の削除
     * @param {object} item 削除対象の明細行
     */
    deleteRow(item) {
      if (!window.confirm(this.$t("shukkaPreDetail.message.deleteRow"))) return;
      this.isSubmitting = true;
      api.post("/shukka/pre/delete", {
        preManagementId: this.preManagementId,
        lineNo: item.lineNumber,
        janCode: item.janCode,
        searchDatetime: this.searchDatetime,
      }).then((response) => {
        if (this.handleResponseError(response)) {
          this.isSubmitting = false;
          return;
        }
        this.isSubmitting = false;
        this.loadDetail();
      }).catch((error) => {
        this.isSubmitting = false;
        this.handleError(error);
      });
    },
    /**
     * 伝票全体の削除
     */
    deleteSlip() {
      if (!window.confirm(this.$t("shukkaPreDetail.message.delete"))) return;
      this.isSubmitting = true;
      api.post("/shukka/pre/delete", {
        preManagementId: this.preManagementId,
        searchDatetime: this.searchDatetime,
      }).then((response) => {
        if (this.handleResponseError(response)) {
          this.isSubmitting = false;
          return;
        }
        this.isSubmitting = false;
        this.back();
      }).catch((error) => {
        this.isSubmitting = false;
        this.handleError(error);
      });
    },
    /**
     * 伝票全体の確定と通常の出荷実績への反映
     */
    confirmSlip() {
      if (!(this.confirmedShukkaDate ?? "").trim()) {
        this.showMessage(
          this.$t("common.message.error"),
          [{ message: `${this.$t("shukkaPreDetail.label.confirmedShukkaDate")}を${this.$t("shukkaPreDetail.validation.required")}` }]
        );
        return;
      }
      this.confirmDialogData = {
        message: this.$t("shukkaPreDetail.message.confirm"),
      };
      this.confirmDialogVisible = true;
    },
    /**
     * 確定ダイアログのOK処理
     * @param {string} staffId 確定担当者ID
     */
    confirmSlipOk(staffId) {
      this.isSubmitting = true;
      return api.post("/shukka/pre/confirm", {
        preManagementId: this.preManagementId,
        confirmedShukkaDate: (this.confirmedShukkaDate ?? "").replaceAll("-", "") + "000000",
        staffId,
        searchDatetime: this.searchDatetime,
      }).then((response) => {
        if (this.handleResponseError(response)) {
          this.isSubmitting = false;
          this.closeConfirmDialog();
          return;
        }
        this.isSubmitting = false;
        this.closeConfirmDialog();
        this.back();
      }).catch((error) => {
        this.isSubmitting = false;
        this.closeConfirmDialog();
        this.handleError(error);
      });
    }
  },
  mounted() {
    // 確定出荷日の初期値を本日の日付に設定
    const today = this.getTodayStartDate();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    this.confirmedShukkaDate = `${yyyy}-${mm}-${dd}`;
    this.$refs.confirmedShukkaDate?.setDate(today);
    this.loadConfirmedShukkaDateRange();
    this.loadDetail();
  }
}
</script>
